import { useEffect, useMemo, useState } from 'react'

import { useQuery } from 'common/hooks/use-query'

import { useVendorStores } from 'vendor/hooks/use-stores'
import { OrderSessionOwner, UpdateOrderSessionResponse } from 'vendor/server/order_sessions'
import { Order } from 'vendor/server/orders'

export const useLockPo = (order: Order) => {
  const { orderStore, userStore } = useVendorStores()

  const featureFlagOrderBlockingEnabled = order?.order_blocking_enabled
  const userIsImpersonating = userStore.currentUser?.impersonating

  const { data, isLoading } = useQuery<UpdateOrderSessionResponse>(
    async () => {
      const name = `${userStore.currentUser.first_name} ${userStore.currentUser.last_name}`
      const user = { id: userStore.currentUser.id, name }
      return orderStore.updateOrderSession(order.id, user)
    },
    [order?.id],
    !!order?.id,
  )

  const orderBlockingEnabled = featureFlagOrderBlockingEnabled && !!order?.id && !data?.disabled

  const [showOrderLockedState, setShowOrderLocked] = useState(false)

  useEffect(() => {
    if (orderBlockingEnabled) {
      const isExternalUser = data?.ownerUser?.id !== userStore.currentUser.id
      if (isExternalUser) {
        setShowOrderLocked(true)
      }
    }
  }, [data, orderBlockingEnabled])

  useEffect(() => {
    if (order) orderStore.orderSessionSubscribe(order.id, userStore.currentUser?.id)
    return () => {
      if (order) orderStore.orderSessionUnsubscribe(order.id)
    }
  }, [order?.id])

  const orderSession = useMemo(() => {
    if (!order?.id) {
      return {
        isBlocked: false,
        isReleased: false,
        externalUser: null as OrderSessionOwner | null,
        modalOrderLockedOpened: false,
        modalOrderExpiredOpened: false,
        modalOrderReleasedOpened: false,
        onCloseModalOrderLocked: () => null,
        onRelease: () => null,
        onRenew: () => null,
        onUpdate: () => null,
      }
    }

    const userInOrder = orderStore.orderSessionOwner
    const externalUser = userInOrder?.id !== userStore.currentUser.id && userInOrder
    const isBlocked = orderBlockingEnabled && externalUser && !isLoading
    const isReleased = !isLoading && !userInOrder
    const modalOrderLockedOpened = orderBlockingEnabled && showOrderLockedState && !!externalUser
    const modalOrderExpiredOpened = orderBlockingEnabled && userInOrder?.expired
    const modalOrderReleasedOpened = orderBlockingEnabled && isReleased && !userIsImpersonating

    const onCloseModalOrderLocked = () => setShowOrderLocked(false)

    const onRenew = () => {
      if (orderBlockingEnabled) {
        orderStore.updateOrderSession(order.id, {
          id: userStore.currentUser.id,
          name: userStore.currentUser.first_name,
        })
      }
    }

    const onRelease = () => orderStore.releaseOrderSession(order.id)

    const onUpdate = () => {
      if (orderBlockingEnabled) orderStore.selectOrder(order.id)
      orderStore.updateOrderSession(order.id, {
        id: userStore.currentUser.id,
        name: userStore.currentUser.first_name,
      })
    }

    return {
      onCloseModalOrderLocked,
      externalUser,
      isBlocked,
      isReleased,
      modalOrderExpiredOpened,
      modalOrderLockedOpened,
      modalOrderReleasedOpened,
      onRelease,
      onRenew,
      onUpdate,
    }
  }, [
    isLoading,
    order?.id,
    orderBlockingEnabled,
    orderStore.orderSessionOwner,
    userStore.currentUser.id,
    userStore.currentUser.first_name,
    showOrderLockedState,
    userIsImpersonating,
  ])

  useEffect(() => {
    /* 
      If the feature is disabled, the process of entering the session becomes automatic, 
      so whoever calls the request first is saved in it. If it is enabled, the process 
      involves clicking on the modal to access the session.
    */
    if (!featureFlagOrderBlockingEnabled && orderSession?.isReleased) {
      orderSession?.onUpdate()
    }
  }, [orderSession?.isReleased])

  useEffect(() => {
    if (orderStore.selectedOrderDirty) {
      orderStore.updateOrderSession(
        order.id,
        {
          id: userStore.currentUser.id,
          name: userStore.currentUser.first_name,
        },
        { order_changed: true },
      )
    }
  }, [orderStore.selectedOrderDirty])

  useEffect(() => {
    return () => {
      orderStore.orderSessionOwner = null
    }
  }, [])

  return {
    orderLocked: orderSession.isBlocked,
    orderSession,
  }
}
